import { Fragment, useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import { relativeTime, absoluteTime } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.js';
import { useCapabilities } from '../../lib/capabilities.js';
import type { WebhookDelivery, WebhookEndpoint } from '../../lib/types.js';

export function WebhooksScreen(): ReactNode {
  const endpoints = useQuery(() => api.webhooks(), []);
  const caps = useCapabilities();
  const { can } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState<WebhookEndpoint | 'new' | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const manage = can('webhooks.manage');
  const events = caps?.webhookEvents ?? [];

  async function toggleEnabled(e: WebhookEndpoint): Promise<void> {
    try {
      await api.updateWebhook(e.id, { enabled: !e.enabled });
      endpoints.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function remove(e: WebhookEndpoint): Promise<void> {
    if (!confirm(`Delete the webhook to ${e.url}?`)) return;
    try {
      await api.deleteWebhook(e.id);
      endpoints.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function ping(e: WebhookEndpoint): Promise<void> {
    try {
      const res = await api.pingWebhook(e.id);
      toast.push(res.ok ? 'Test delivered' : 'Test failed — check deliveries', res.ok ? 'success' : 'error');
      endpoints.refetch();
      setExpanded(e.id);
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="Webhooks"
      actions={
        manage ? (
          <button className="btn primary" onClick={() => setEditing('new')}>
            Add endpoint
          </button>
        ) : undefined
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        Akarya <code>POST</code>s a signed JSON payload to these URLs when events happen. See{' '}
        <code>docs/spec/webhooks.md</code> for the signature scheme.
      </p>

      {endpoints.loading && !endpoints.data ? <Loading what="webhooks" /> : null}
      {endpoints.error ? <ErrorNote error={endpoints.error} /> : null}

      {endpoints.data ? (
        endpoints.data.length === 0 ? (
          <EmptyState>No webhook endpoints yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Events</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {endpoints.data.map((e) => (
                  <Fragment key={e.id}>
                    <tr>
                      <td className="mono small" style={{ wordBreak: 'break-all' }}>
                        {e.url}
                        {e.description ? <div className="faint">{e.description}</div> : null}
                      </td>
                      <td className="small">
                        {e.events.includes('*') ? 'all events' : e.events.join(', ')}
                      </td>
                      <td className="small">
                        {e.enabled ? (
                          <span className="badge good">enabled</span>
                        ) : (
                          <span className="badge bad">disabled</span>
                        )}
                        {e.consecutiveFailures > 0 ? (
                          <span className="faint"> · {e.consecutiveFailures} fails</span>
                        ) : null}
                        {e.lastSuccessAt ? (
                          <div className="faint" title={absoluteTime(e.lastSuccessAt)}>
                            ok {relativeTime(e.lastSuccessAt)}
                          </div>
                        ) : null}
                      </td>
                      <td onClick={(ev) => ev.stopPropagation()}>
                        <div className="row" style={{ gap: 6 }}>
                          <button
                            className="btn small"
                            onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          >
                            Deliveries
                          </button>
                          {manage ? (
                            <>
                              <button className="btn small" onClick={() => void ping(e)}>
                                Test
                              </button>
                              <button className="btn small" onClick={() => void toggleEnabled(e)}>
                                {e.enabled ? 'Disable' : 'Enable'}
                              </button>
                              <button className="btn small" onClick={() => setEditing(e)}>
                                Edit
                              </button>
                              <button className="btn small danger" onClick={() => void remove(e)}>
                                Delete
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {expanded === e.id ? (
                      <tr>
                        <td colSpan={4}>
                          <DeliveryList endpointId={e.id} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {editing ? (
        <EndpointModal
          endpoint={editing === 'new' ? null : editing}
          events={events}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            endpoints.refetch();
            if (created?.secret) setSecret(created.secret);
          }}
        />
      ) : null}

      {secret ? (
        <Modal title="Signing secret" onClose={() => setSecret(null)}>
          <p className="muted small">
            Shown once. Use it to verify the <code>X-Akarya-Signature</code> header on every delivery.
          </p>
          <div className="mono" style={{ wordBreak: 'break-all', padding: '10px 0' }}>{secret}</div>
          <div className="row between">
            <button
              className="btn small"
              onClick={() => void navigator.clipboard?.writeText(secret)}
            >
              Copy
            </button>
            <button className="btn primary small" onClick={() => setSecret(null)}>
              Done
            </button>
          </div>
        </Modal>
      ) : null}
    </Page>
  );
}

function DeliveryList({ endpointId }: { endpointId: string }): ReactNode {
  const deliveries = useQuery(() => api.webhookDeliveries(endpointId), [endpointId]);

  if (deliveries.loading && !deliveries.data) return <Loading what="deliveries" />;
  if (deliveries.error) return <ErrorNote error={deliveries.error} />;
  if (!deliveries.data || deliveries.data.length === 0)
    return <div className="faint small" style={{ padding: 6 }}>No deliveries yet.</div>;

  const badge: Record<WebhookDelivery['status'], string> = {
    delivered: 'good',
    pending: 'warn',
    failed: 'warn',
    dead: 'bad',
  };

  return (
    <table className="grid" style={{ margin: 4 }}>
      <tbody>
        {deliveries.data.map((d) => (
          <tr key={d.id}>
            <td className="mono small">{d.event}</td>
            <td className="small">
              <span className={`badge ${badge[d.status]}`}>{d.status}</span>
            </td>
            <td className="faint small">
              {d.attempts} attempt{d.attempts === 1 ? '' : 's'}
              {d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
              {d.error ? ` · ${d.error}` : ''}
            </td>
            <td className="faint small" title={absoluteTime(d.createdAt)}>
              {relativeTime(d.createdAt)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EndpointModal({
  endpoint,
  events,
  onClose,
  onSaved,
}: {
  endpoint: WebhookEndpoint | null;
  events: { name: string; label: string }[];
  onClose: () => void;
  onSaved: (created: WebhookEndpoint | null) => void;
}): ReactNode {
  const toast = useToast();
  const [url, setUrl] = useState(endpoint?.url ?? '');
  const [description, setDescription] = useState(endpoint?.description ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set(endpoint?.events ?? []));
  const [busy, setBusy] = useState(false);

  function toggle(name: string): void {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const body = {
        url: url.trim(),
        description: description.trim() || null,
        events: [...picked],
      };
      if (endpoint) {
        await api.updateWebhook(endpoint.id, body);
        onSaved(null);
      } else {
        const created = await api.createWebhook(body);
        onSaved(created);
      }
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title={endpoint ? 'Edit webhook' : 'Add webhook'} onClose={onClose} wide>
      <div className="stack">
        <label className="field">
          Payload URL
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoFocus />
        </label>
        <label className="field">
          Description (optional)
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="field">
          Events
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={picked.has('*')} onChange={() => toggle('*')} />
            <span className="small">All events</span>
          </label>
          {events.map((ev) => (
            <label key={ev.name} className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={picked.has(ev.name)}
                disabled={picked.has('*')}
                onChange={() => toggle(ev.name)}
              />
              <span className="mono small">{ev.name}</span>
              <span className="muted small">{ev.label}</span>
            </label>
          ))}
        </div>
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={busy || url.trim() === '' || picked.size === 0}
            onClick={() => void submit()}
          >
            {busy ? 'Saving…' : endpoint ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
